<script lang="ts">
import { defineComponent, PropType, useSlots, computed } from 'vue'
import { defaultProps, defaultFunction } from '@/components/_parials/buttonCore'

export default defineComponent({
  name: 'ButtonDefault',

  props: {
    ...defaultProps,

    color: {
      type: String as PropType<'gray' | 'orange'>,
      default: 'gray',
    },

    isActive: {
      type: Boolean,
      default: false,
    },

    iconUrl: {
      type: Object as PropType<{
        default: string,
        hover: string,
        active: string,
      }>,
      default: () => ({
        default: '',
        hover: '',
        active: '',
      }),
    },
  },

  setup(props, { emit }) {
    const {
      // data
      wrapper,

      // methods
      runEvent,
    } = defaultFunction(props, emit)

    const slots = useSlots()

    const hasLabel = computed<boolean>(() => {
      const v = !!slots.default && slots.default().some(vnode => vnode.children || vnode.props)
      if (!v) {
        console.warn('Exception:\nButton label slot is empty. Please provide content into the slot.')
      }
      return v
    })

    const hasIcon = computed<boolean>(() => {
      return !!props.iconUrl.default
    })

    const computedIconUrl = computed<{
      default: string,
      hover: string,
      active: string,
    }>(() => {
      return {
        default: props.iconUrl.default,
        hover: props.iconUrl.hover || props.iconUrl.default,
        active: props.iconUrl.active || props.iconUrl.default,
      }
    })

    return {
      // data
      wrapper,

      // computed
      hasLabel,
      hasIcon,
      computedIconUrl,

      // methods
      runEvent,
    }
  }
})
</script>

<template>
  <div
    v-if="hasLabel"
    ref="wrapper"
    :class="[
      'o-action',
      { 'is-gray': color === 'gray' },
      { 'is-orange': color === 'orange' },
      { 'is-active': isActive },
      { 'is-small': isFontSmall },
      { 'is-bold': isFontBold },
      { 'is-shadow': hasShadow },
      { 'is-autoWidth': autoWidth },
      { 'is-large': isLarge },
      { 'is-narrowMargin': narrowMargin },
    ]"
    :style="{ '--max-width': width ? `${width}px` : 'none' }">
    <a
      v-if="isLink"
      class="o-action--item"
      :href="href"
      :target="target">
      <span
        v-if="hasIcon"
        class="o-action--icon">
        <i
          class="i"
          :style="{
            '--icon-url': `url(${computedIconUrl.default})`,
            '--icon-url-hover': `url(${computedIconUrl.hover})`,
            '--icon-url-active': `url(${computedIconUrl.active})`,
          }" />
      </span>
      <span class="o-action--label">
        <slot />
      </span>
    </a>
    <button
      v-else
      class="o-action--item"
      type="button"
      :name="name"
      :disabled="disabled"
      @click="runEvent">
      <span
        v-if="hasIcon"
        class="o-action--icon">
        <i
          class="i"
          :style="{
            '--icon-url': `url(${computedIconUrl.default})`,
            '--icon-url-hover': `url(${computedIconUrl.hover})`,
            '--icon-url-active': `url(${computedIconUrl.active})`,
          }" />
      </span>
      <span class="o-action--label">
        <slot />
      </span>
    </button>
  <!-- /.o-action --></div>
</template>

<style lang="sass" scoped>
.o-action
  $root: &
  $margin-size-px: 24
  $margin-narrow-size-px: 16
  --max-width: none
  --icon-url: url()
  --icon-url-hover: url()
  --icon-url-active: url()
  margin:
    top: #{math.div($margin-size-px, $font-normal-px)}em
    left: auto
    right: auto

  &:first-child
    margin-top: 0

  & &--item
    $button-height-px: 48
    width: 100%
    max-width: var(--max-width)
    min-height: changeSizePx2Em($button-height-px)
    display: flex
    justify-content: center
    align-items: center
    line-height: 1.4
    font-size: $font-normal
    text:
      align: center
      decoration: none
    margin:
      left: auto
      right: auto
    padding: 0.5em 1.2em
    border-radius: 100vh
    +transition($duration: $transition-middle)

    &:not(:disabled)
      +mouseover
        #{$root}--icon
          > .i
            background-image: var(--icon-url-hover)

  & &--icon
    display: block
    line-height: 1
    margin-right: 0.3em

    > .i
      width: changeSizePx2Em(24)
      height: changeSizePx2Em(24)
      display: block
      background:
        image: var(--icon-url)
        size: contain
        repeat: no-repeat
        position: center

  & &--label
    display: block

  &.is-active
    #{$root}--icon
      > .i
        background-image: var(--icon-url-active)

  &.is-gray
    #{$root}--item
      color: $font-gray
      background-color: rgba($bg-white, 1)
      border: 1px solid $border-gray

      &:not(:disabled)
        +mouseover
          color: $font-orange
          border-color: $border-orange

      &:disabled
        color: rgba($font-gray, 0.5)
        background-color: transparent
        border: 1px solid rgba($border-gray, 0.5)

    &.is-active
      #{$root}--item
        color: $font-orange
        border-color: $border-orange

  &.is-orange
    #{$root}--item
      color: $font-orange
      background-color: $bg-white
      border: 1px solid $border-orange

      &:not(:disabled)
        +mouseover
          background-color: $bg-xx-light-orange
          border-color: $border-orange

      &:disabled
        color: rgba($font-gray, 0.5)
        background-color: transparent
        border-color: rgba($border-gray, 0.5)

    &.is-active
      #{$root}--item
        background-color: $bg-xx-light-orange
        border-color: $border-orange

  &.is-small
    $button-height-px: 40
    #{$root}--item
      min-height: changeSizePx2Em($button-height-px, $font-small-px)
      font-size: $font-small
      padding: 0.5em 1.8em

  &.is-bold
    #{$root}--item
      font-weight: bold

  &.is-shadow
    #{$root}--item
      &:not(:disabled)
        +mouseover
          box-shadow: $button-shadow

  &.is-autoWidth
    display: flex
    justify-content: center

    #{$root}--item
      width: auto

  &.is-narrowMargin
    margin-top: #{math.div($margin-narrow-size-px, $font-normal-px)}em

    &:first-child
      margin-top: 0

  &.is-large
    $button-height-px: 54
    #{$root}--item
      min-height: changeSizePx2Em($button-height-px)
      padding:
        top: 0.6em
        bottom: 0.6em

  +breakpoint
    $margin-size-px: 16
    margin-top: #{math.div($margin-size-px, $font-normal-px)}em

    &:first-child
      margin-top: 0
</style>
